import { useDialogHandler } from '@travel/traveler-core/hooks';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

type Props = {
  fadeTimer: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  hideTimer: MutableRefObject<ReturnType<typeof setTimeout> | null>;
  fadingTime?: number;
  hidingTime?: number;
  endTimerHandler?: () => void;
};

const useActionTimers = (props: Props) => {
  const { fadeTimer, hideTimer, fadingTime = 1000, hidingTime = 3000, endTimerHandler } = props;
  const { onOpen, onClose, isOpen } = useDialogHandler(false);
  const [isFading, setIsFading] = useState<boolean>(false);

  const initializePopUpState = useCallback(() => {
    onClose();
    setIsFading(false);
  }, [onClose]);

  const clearTimers = useCallback(() => {
    if (fadeTimer.current) clearTimeout(fadeTimer.current);
    if (hideTimer.current) clearTimeout(hideTimer.current);
  }, [fadeTimer, hideTimer]);

  const startTimers = useCallback(() => {
    clearTimers();
    fadeTimer.current = setTimeout(() => setIsFading(true), fadingTime);
    hideTimer.current = setTimeout(() => {
      initializePopUpState();
      endTimerHandler && endTimerHandler();
    }, hidingTime);
  }, [
    clearTimers,
    endTimerHandler,
    fadeTimer,
    fadingTime,
    hideTimer,
    hidingTime,
    initializePopUpState,
  ]);

  const showPopUp = useCallback(() => {
    onOpen();
    startTimers();
  }, [onOpen, startTimers]);

  useEffect(() => {
    if (isOpen) startTimers();
    return () => {
      clearTimers();
    };
  }, [isOpen, startTimers, clearTimers]);

  return { isFading, isOpen, initializePopUpState, showPopUp };
};

export default useActionTimers;
